import React, { useMemo, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { isSuccessWithData, useFirebase } from 'src/firebase/useFirebase'
import { useAdvancedResource } from '../api/useAdvancedResource'
import SectionCard from '../components/SectionCard'
import { roleLabelFor } from '../dashboard/careTeam/roleCategories'
import { joinName } from '../format'
import { advancedColors } from '../theme'
import { loadMembershipReview, reviewMembershipsByProvider } from './adminMembershipReview'

type ProviderProfile = { firstname?: string; lastname?: string }
const TITLE = 'Review care team memberships per provider'

const AdvancedAdminMembershipReview = () => {
  const client = useApolloClient()
  const profiles = useFirebase<Record<string, ProviderProfile>>('coachProfiles')
  const [requested, setRequested] = useState(false)
  const [version, setVersion] = useState(0)
  const [filter, setFilter] = useState('')
  const [onlyMissing, setOnlyMissing] = useState(false)
  const resource = useAdvancedResource((signal) => loadMembershipReview(client, signal), [client, version], !requested)
  const refresh = () => setVersion((current) => current + 1)
  const review = resource.status === 'ready' ? resource.data : null
  const providerName = (uid: string) => {
    const profile = isSuccessWithData(profiles) ? profiles.data[uid] : undefined
    return joinName(profile?.firstname, profile?.lastname) || uid
  }
  const patients = useMemo(() => new Map(review?.patients.map((patient) => [patient.uid, patient]) ?? []), [review])
  const patientName = (uid: string) => {
    const patient = patients.get(uid)
    return joinName(patient?.firstname, patient?.lastname) || uid
  }
  const byPatientName = (a: string, b: string) => patientName(a).localeCompare(patientName(b)) || a.localeCompare(b)
  const providers = useMemo(
    () => (review ? reviewMembershipsByProvider(review.memberships, review.patients) : []),
    [review]
  )
  const comparisonComplete = !!review?.hasAdvancedTag && review.unavailablePatientUids.length === 0
  const noMissingLabel = comparisonComplete ? 'None' : 'Not fully checked'
  const query = filter.trim().toLowerCase()
  const visible = providers
    .filter(({ providerUid }) => `${providerName(providerUid)} ${providerUid}`.toLowerCase().includes(query))
    .filter(
      (provider) =>
        !onlyMissing ||
        provider.missingMembershipPatients.length > 0 ||
        provider.missingStandardMembershipPatients.length > 0
    )
    .sort(
      (a, b) =>
        providerName(a.providerUid).localeCompare(providerName(b.providerUid)) ||
        a.providerUid.localeCompare(b.providerUid)
    )

  return (
    <SectionCard
      title={TITLE}
      collapsible
      help="This helps you check if providers are correctly set up for operating in the Advanced dashboard. It shows both the providers' standard and advanced memberships. In most cases they should all match."
      onExpandedChange={(expanded) => {
        if (expanded) setRequested(true)
      }}
    >
      {resource.status === 'loading' && <LinearProgress />}
      {resource.status === 'error' && (
        <Alert severity="error" action={<Button onClick={refresh}>Retry</Button>}>
          Failed to load the membership review. Both Advanced memberships and Standard care teams are needed for the
          comparison.
        </Alert>
      )}
      {review && (
        <>
          {!review.hasAdvancedTag && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The Advanced program tag was not found. Standard caseload checks include only patients with Advanced
              memberships.
            </Alert>
          )}
          {review.unavailablePatientUids.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Standard care team details could not be read for {review.unavailablePatientUids.length} patients.
              Missing-membership results may be incomplete.
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                fullWidth
                label="Find provider"
                size="small"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                sx={{ mt: 0, flex: 1, minWidth: 0 }}
              />
              <Button onClick={refresh} sx={{ flexShrink: 0 }}>
                Refresh
              </Button>
            </Box>
            <FormControlLabel
              sx={{ m: 0, mt: 1 }}
              control={
                <Checkbox size="small" checked={onlyMissing} onChange={(_event, checked) => setOnlyMissing(checked)} />
              }
              label="Only providers with missing memberships"
            />
          </Box>
          {visible.length === 0 ? (
            <Typography variant="body2">
              {providers.length === 0
                ? 'No Advanced care team memberships or Standard caseload patients found.'
                : 'No providers match.'}
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 520, border: `1px solid ${advancedColors.line}`, borderRadius: 1 }}>
              <Table size="small" stickyHeader aria-label="Care team memberships by provider">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell>Advanced care team patients</TableCell>
                    <TableCell>Missing Advanced membership</TableCell>
                    <TableCell>Missing standard membership</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((provider) => {
                    const missingCount =
                      provider.missingMembershipPatients.length + provider.missingStandardMembershipPatients.length
                    const noMissingStandardLabel = provider.advancedPatients.some(
                      ({ uid }) => patients.get(uid)?.careTeam == null
                    )
                      ? 'Not fully checked'
                      : 'None'
                    return (
                      <TableRow key={provider.providerUid}>
                        <TableCell component="th" scope="row" sx={{ verticalAlign: 'top' }}>
                          <Typography variant="body2">{providerName(provider.providerUid)}</Typography>
                          {missingCount > 0 && (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`${missingCount} missing`}
                              sx={{ mt: 1 }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {provider.advancedPatients.length === 0 ? (
                            'None'
                          ) : (
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                              {[...provider.advancedPatients]
                                .sort((a, b) => byPatientName(a.uid, b.uid))
                                .map((patient) => (
                                  <li key={patient.uid}>
                                    <Typography variant="body2">{patientName(patient.uid)}</Typography>
                                    <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
                                      {patient.roles.map(roleLabelFor).join(', ')}
                                    </Typography>
                                  </li>
                                ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {provider.missingMembershipPatients.length === 0 ? (
                            noMissingLabel
                          ) : (
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                              {[...provider.missingMembershipPatients].sort(byPatientName).map((uid) => (
                                <li key={uid}>
                                  <Typography variant="body2">{patientName(uid)}</Typography>
                                </li>
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {provider.missingStandardMembershipPatients.length === 0 ? (
                            noMissingStandardLabel
                          ) : (
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                              {[...provider.missingStandardMembershipPatients].sort(byPatientName).map((uid) => (
                                <li key={uid}>
                                  <Typography variant="body2">{patientName(uid)}</Typography>
                                </li>
                              ))}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </SectionCard>
  )
}

export default AdvancedAdminMembershipReview
